/**
 * useExposureCohorts — TanStack Query wrapper for `exposure.list`.
 *
 * Fetches cohorts via the tRPC HTTP transport (plain fetch, no @trpc/client
 * overhead). Each cohort carries a `windowClosesAt` timestamp computed
 * server-side as GREATEST(boardedAt, disembarkedAt, identifiedAt) + 39 days.
 *
 * Dates arrive as ISO strings over the wire and are normalised to Date objects
 * here so the countdown component can work purely with Date arithmetic.
 */

import { useQuery } from "@tanstack/react-query";

export type ExposureCohort = {
  id: number;
  groupLabel: string;
  headcount: number | null;
  status: string;
  boardedAt: Date | null;
  disembarkedAt: Date | null;
  identifiedAt: Date;
  windowClosesAt: Date | null;
  closedAt: Date | null;
  notes: string | null;
  sourceUrl: string;
  verifiedAt: Date;
};

type RawCohort = {
  id: number;
  groupLabel: string;
  headcount: number | null;
  status: string;
  boardedAt: string | null;
  disembarkedAt: string | null;
  identifiedAt: string;
  windowClosesAt: string | null;
  closedAt: string | null;
  notes: string | null;
  sourceUrl: string;
  verifiedAt: string;
};

function toDate(s: string | null): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalise(raw: RawCohort): ExposureCohort {
  return {
    ...raw,
    boardedAt: toDate(raw.boardedAt),
    disembarkedAt: toDate(raw.disembarkedAt),
    identifiedAt: new Date(raw.identifiedAt),
    windowClosesAt: toDate(raw.windowClosesAt),
    closedAt: toDate(raw.closedAt),
    verifiedAt: new Date(raw.verifiedAt),
  };
}

async function fetchExposureCohorts(
  slug: string,
  apiBaseUrl?: string,
): Promise<ExposureCohort[]> {
  const base =
    (apiBaseUrl ?? (typeof import.meta !== "undefined"
      ? (import.meta.env?.VITE_API_URL as string | undefined)
      : undefined) ?? "").replace(/\/+$/u, "");

  const encoded = encodeURIComponent(
    JSON.stringify({ json: { slug, limit: 50 } }),
  );
  const url = `${base}/api/trpc/exposure.list?input=${encoded}`;

  const res = await fetch(url, {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "omit",
  });
  if (!res.ok) throw new Error(`exposure.list failed: ${res.status}`);

  const json = (await res.json()) as {
    result?: { data?: { json?: RawCohort[] } | RawCohort[] };
  };
  const inner = json.result?.data;
  const data =
    inner && typeof inner === "object" && "json" in inner
      ? (inner as { json: RawCohort[] }).json
      : (inner as RawCohort[] | undefined);
  if (!data) throw new Error("exposure.list: malformed response");
  return data.map(normalise);
}

export function useExposureCohorts(
  slug: string,
  opts?: { apiBaseUrl?: string; enabled?: boolean },
) {
  return useQuery<ExposureCohort[]>({
    queryKey: ["exposure", "list", slug],
    queryFn: () => fetchExposureCohorts(slug, opts?.apiBaseUrl),
    enabled: opts?.enabled !== false && Boolean(slug),
    staleTime: 60_000,
  });
}
